import { z } from 'zod';
import { digestSchema } from '../hashing/hash.js';
import { personIdSchema } from '../people/person.js';
import { tokenPurposeSchema } from './tokens.js';
/**
 * Self-service link reissue (#82, ADR-004 "email me a fresh link").
 *
 * Two doors, one answer. `reissue-from-token` is opened by someone who already holds the link
 * (or a session made from it), so it reveals nothing they did not have. `reissue` takes an
 * address typed by anyone at all, and is therefore an enumeration oracle unless every possible
 * outcome — known, unknown, ineligible, rate-limited, malformed, and the database throwing —
 * produces the SAME status, headers and body. That response is written exactly once, below.
 *
 * The link always goes to the address ON FILE. The typed address only finds the person; it is
 * never a destination, never stored, and never logged.
 */
/** A token from the link, or nothing — the mid-form 401 has only its session cookie. */
export const reissueFromTokenRequestSchema = z
    .object({ token: z.string().min(1).max(512).optional() })
    .strict();
/** RFC 5321 caps a path at 254 octets; anything longer is not an address we could hold. */
export const reissueByEmailRequestSchema = z
    .object({ email: z.string().trim().min(3).max(254).email() })
    .strict();
/** Case-folded and trimmed. Nothing cleverer: a `+tag` or a dot is part of somebody's address. */
export function normalizeReissueEmail(email) {
    return email.trim().toLowerCase();
}
export const reissueAcceptedSchema = z
    .object({ ok: z.literal(true), message: z.string().min(1) })
    .strict();
/**
 * THE response, for both endpoints and every outcome. Frozen, and the same object every time,
 * so no branch can build a near-copy that differs by a word or a header.
 *
 * The wording is true in every case, which is the point: it promises nothing that would only be
 * true for an address we hold.
 */
export const REISSUE_ACCEPTED = Object.freeze({
    status: 202,
    headers: Object.freeze({ 'Cache-Control': 'no-store' }),
    body: Object.freeze(reissueAcceptedSchema.parse({
        ok: true,
        message: 'If we can, we will email a new link to the address we have on file for you. It can take a few minutes to arrive. Use the access code you already have.',
    })),
});
/**
 * Fixed windows, counted app-side: Y1 Consumption with public network access and no WAF or
 * Front Door means there is no edge to lean on (#82).
 *
 * The token door has its own IP budget and never touches the global one — flooding the email
 * door must not be able to lock out the path that has no enumeration surface at all.
 */
export const REISSUE_LIMITS = Object.freeze({
    global: { limit: 200, windowSeconds: 60 * 60 },
    ip: { limit: 10, windowSeconds: 60 * 60 },
    address: { limit: 3, windowSeconds: 24 * 60 * 60 },
    tokenIp: { limit: 10, windowSeconds: 60 * 60 },
});
/** The start of the fixed window `now` falls in, as an ISO instant. Pure, so windows can be asserted. */
export function reissueWindowStart(now, windowSeconds) {
    const ms = windowSeconds * 1000;
    return new Date(Math.floor(Date.parse(now) / ms) * ms).toISOString();
}
/**
 * A counter document, in the `tokens` container because it is the one container with TTL
 * enabled. The id IS the digest of (scope, key, window), so the address or IP it counts is
 * nowhere in it. `tokenHash` mirrors the id because that container partitions on it.
 */
export const reissueLimitCounterSchema = z
    .object({
    id: digestSchema,
    tokenHash: digestSchema,
    docType: z.literal('reissue-limit'),
    count: z.number().int().positive(),
    ttl: z.number().int().positive(),
})
    .strict();
/**
 * Who may recover a link without HR. An allow-list, so a lifecycle added later is refused until
 * somebody decides otherwise.
 *
 * `inactive` (leave, suspension) is refused and goes to HR — decided by Julio, 2026-09-16 (#82).
 */
export function isReissuableLifecycle(lifecycle) {
    return lifecycle === 'candidate' || lifecycle === 'onboarding' || lifecycle === 'active';
}
/**
 * Whether a link may be replaced by the person themselves.
 *
 * Revoked links are HR's. So is a link with ANY wrong access code against it, locked or not:
 * a reissue keeps the code, and a fresh record starts its failure count at zero — so allowing
 * it would hand a guesser seven new tries per reissue, forever, and the revocation at eight
 * that is meant to reach HR never would (#82 security review).
 */
export function isReissuableSourceToken(record) {
    return record.revokedAt === null && record.failedAttempts === 0 && record.lockedUntil === null;
}
/** Step states a person can still act on. Anything else is submitted, approved or cancelled. */
const OPEN_STEP_STATES = ['not_started', 'sent', 'in_progress', 'rejected'];
/**
 * A document-request link is worth replacing only while something it covers is still open.
 * An empty scope, or one whose items are all done, is refused and goes to HR — decided by Julio,
 * 2026-09-16 (#82).
 */
export function docRequestHasOpenItems(scopedInstanceIds, instances) {
    const open = new Set(instances.filter((i) => OPEN_STEP_STATES.includes(i.state)).map((i) => i.id));
    return scopedInstanceIds.some((id) => open.has(id));
}
export const reissueRequestStatusSchema = z.enum([
    /** Recorded; the drain has not taken it. */
    'pending',
    /** Claimed by one drain run. Found here later means that run died — it is failed, never retried. */
    'processing',
    'sent',
    /** No longer eligible when the drain looked: HR revoked it, or another link replaced it. */
    'skipped',
    'failed',
    /** The mail layer parked the message for an operator. */
    'parked',
]);
/**
 * A pending reissue, in the existing `communications` container (#82: no outbox container).
 *
 * It holds identifiers only: the person, the digest of the link it replaces (once known), and
 * how it was asked for. No address, no IP, no token, no code — the drain reads the address from
 * the person record at send time, which is what guarantees the link goes to the address on file.
 */
export const reissueRequestSchema = z
    .object({
    id: z.string().min(1),
    personId: personIdSchema,
    docType: z.literal('reissue-request'),
    purpose: tokenPurposeSchema,
    /**
     * The link being replaced. Known at request time for the token door. NULL for the email
     * door: resolving it there would cost a known address three extra reads that an unknown one
     * does not pay, and the difference is a stopwatch oracle. The drain resolves it instead.
     */
    sourceTokenHash: digestSchema.nullable(),
    channel: z.enum(['token', 'email']),
    requestedAt: z.string().datetime(),
    status: reissueRequestStatusSchema,
    /** The `token.re-requested` row is written by the drain, off the request path, and marked here. */
    audited: z.boolean(),
    /**
     * The link minted for this request, recorded BEFORE the send. If a run dies mid-send, the
     * next one knows which undelivered link to withdraw.
     */
    replacementTokenHash: digestSchema.nullable(),
    claimedAt: z.string().datetime().nullable(),
    resolvedAt: z.string().datetime().nullable(),
    /** Operational words only, never message content. */
    outcome: z.string().max(200).nullable(),
    communicationId: z.string().min(1).nullable(),
})
    .strict();
/**
 * The token door: one request per link being replaced. A second click, a double-submit or a
 * retry meets the record the first one wrote, so a burst is one email, not three.
 */
export function reissueRequestIdForToken(sourceTokenHash) {
    return `reissue:${sourceTokenHash}`;
}
/**
 * The email door: a fresh id per request, because finding the link to key on would cost reads
 * only a known address pays. Duplicates are collapsed by the drain instead, and the per-address
 * limit bounds them to three a day.
 */
export function reissueRequestIdForEmail(requestId) {
    return `reissue:email:${requestId}`;
}
/** A request not delivered within this long is dropped rather than surprising someone later. */
export const REISSUE_REQUEST_MAX_AGE_HOURS = 24;
//# sourceMappingURL=reissue.js.map